import { Bar } from '@ant-design/plots';
import { Empty } from 'antd';
import { useCustomTranslation } from '../../../../hooks/custom-translation';
import { H5 } from '../../../../styles/fonts';

import { Content02, Data012, Data022, Row011 } from './styles';

interface GeographicDistributionTabProps {
  geographicDistribution: {
    states?: {
      description: string;
      quantity: number;
      percentage: number;
    }[];
    cities?: {
      description: string;
      quantity: number;
      percentage: number;
    }[];
  };
}

const GeographicDistributionTab = ({
  geographicDistribution,
}: GeographicDistributionTabProps) => {
  const { tModalDescriptiveAnalysis } = useCustomTranslation(
    'modalDescriptiveAnalysis',
  );

  const hasStatesData = (geographicDistribution.states?.length ?? 0) > 0;
  const hasCitiesData = (geographicDistribution.cities?.length ?? 0) > 0;

  return (
    <Content02>
      <Row011>
        <Data012>
          <H5 weight="bold">
            {tModalDescriptiveAnalysis('distribution_by_state')}
          </H5>
          {hasStatesData ? (
            <>
              <Bar
                data={
                  geographicDistribution.states?.map(state => {
                    const total =
                      geographicDistribution.states?.reduce(
                        (sum, s) => sum + s.quantity,
                        0,
                      ) || 1;
                    const percentage = (state.quantity / total) * 100;
                    return {
                      description: state.description,
                      quantity: state.quantity,
                      percentage,
                    };
                  }) || []
                }
                xField="quantity"
                yField="description"
                seriesField="description"
                color="#d3adf7"
                height={Math.max(
                  400,
                  (geographicDistribution.states?.length || 0) * 25,
                )}
                legend={false}
                barStyle={{
                  radius: [0, 4, 4, 0],
                  stroke: '#9254de',
                }}
                label={{
                  position: 'right',
                  formatter: (datum: any) => {
                    if (!datum.percentage || datum.percentage < 0.1) return '';
                    const hasSpace =
                      (geographicDistribution.states?.length || 0) < 15;
                    return hasSpace
                      ? `${datum.quantity} (${datum.percentage.toFixed(1)}%)`
                      : `${datum.quantity}`;
                  },
                  style: {
                    fill: '#000',
                    fontSize: 10,
                  },
                }}
                xAxis={{
                  grid: {
                    line: {
                      style: {
                        stroke: '#f0f0f0',
                      },
                    },
                  },
                }}
                yAxis={{
                  label: {
                    style: {
                      fill: '#000',
                      fontSize: 10,
                    },
                    autoRotate: false,
                    autoHide: false,
                    formatter: (text: string) => {
                      return text.length > 15
                        ? `${text.substring(0, 15)}...`
                        : text;
                    },
                  },
                }}
                tooltip={{
                  fields: ['description', 'quantity', 'percentage'],
                  formatter: (datum: any) => {
                    return {
                      name: 'Estado',
                      value: `${datum.description}: ${datum.quantity} (${
                        datum.percentage !== undefined &&
                        datum.percentage !== null
                          ? datum.percentage.toFixed(1)
                          : '0.0'
                      }%)`,
                    };
                  },
                }}
              />
            </>
          ) : (
            <Empty
              description="Nenhum dado de estado disponível"
              style={{
                height: 300,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            />
          )}
        </Data012>

        <Data022>
          <H5 weight="bold">
            {tModalDescriptiveAnalysis('distribution_by_city')}
          </H5>
          {hasCitiesData ? (
            <>
              <Bar
                data={
                  geographicDistribution.cities?.map(city => {
                    const total =
                      geographicDistribution.cities?.reduce(
                        (sum, c) => sum + c.quantity,
                        0,
                      ) || 1;
                    const percentage = (city.quantity / total) * 100;
                    return {
                      description: city.description,
                      quantity: city.quantity,
                      percentage,
                    };
                  }) || []
                }
                xField="quantity"
                yField="description"
                seriesField="description"
                color="#d3adf7"
                height={Math.max(
                  400,
                  (geographicDistribution.cities?.length || 0) * 25,
                )}
                legend={false}
                barStyle={{
                  radius: [0, 4, 4, 0],
                  stroke: '#9254de',
                }}
                label={{
                  position: 'right',
                  formatter: (datum: any) => {
                    if (datum.percentage < 0.1) return '';
                    return `${datum.quantity} (${datum.percentage.toFixed(
                      1,
                    )}%)`;
                  },
                  style: {
                    fill: '#000',
                    fontSize: 10,
                  },
                }}
                xAxis={{
                  grid: {
                    line: {
                      style: {
                        stroke: '#f0f0f0',
                      },
                    },
                  },
                }}
                yAxis={{
                  label: {
                    style: {
                      fill: '#000',
                      fontSize: 10,
                    },
                    autoRotate: false,
                    autoHide: false,
                    formatter: (text: string) => {
                      return text.length > 15
                        ? `${text.substring(0, 15)}...`
                        : text;
                    },
                  },
                }}
                tooltip={{
                  fields: ['description', 'quantity', 'percentage'],
                  formatter: (datum: any) => {
                    return {
                      name: 'Cidade',
                      value: `${datum.description}: ${datum.quantity} (${
                        datum.percentage !== undefined &&
                        datum.percentage !== null
                          ? datum.percentage.toFixed(1)
                          : '0.0'
                      }%)`,
                    };
                  },
                }}
              />
            </>
          ) : (
            <Empty
              description="Nenhum dado de cidade disponível"
              style={{
                height: 300,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            />
          )}
        </Data022>
      </Row011>
    </Content02>
  );
};

export default GeographicDistributionTab;
